// Words for a row of the organisation's audit log: what its action and target read as, and the names
// of the venues it happened in.
//
// **Actions and targets** read as their copy (`audit.actions`, `audit.targetType` in the `console`
// namespace); a code with no copy reads as its own words — `shifts.shift.closed` is "Shift closed" —
// so a new action is readable the day it is first written. The humanising is the platform log's, so
// the two never disagree on what a code says.
//
// **Venue names** come from the member's own venues in this organisation (`GET /v1/me/venues`), which
// reading writes no audit row of its own. A venue the member does not work at shows its short id.

import { useTranslation } from '@clavian/i18n/react';
import { useMemo } from 'react';

import { humanise } from '../../lib/json';
import { useMyVenues } from '../../api/queries';
import { useScopeIds } from '../../shell/scope';

/** `shifts.shift.closed` → `shifts__shift__closed`: i18next reads a dot as nesting. */
const copyKey = (code: string): string => code.replaceAll('.', '__');

/**
 * A code's own words: `shifts.shift.closed` → "Shift closed" (the module is dropped when the rest
 * names the thing), `sessions.start` → "Sessions start".
 */
export function humaniseAction(action: string): string {
  const segments = action.split('.');
  return humanise((segments.length > 2 ? segments.slice(1) : segments).join(' '));
}

export interface AuditLabels {
  action(code: string): string;
  targetType(type: string): string;
  /** Every action with copy, by its copy lower-cased: what the search box matches. */
  readonly actionsByLabel: ReadonlyMap<string, string>;
}

export function useAuditLabels(): AuditLabels {
  const { t, i18n } = useTranslation('console');
  return useMemo(() => {
    const copy = (key: string): string | undefined => (i18n.exists(key, { ns: 'console' }) ? t(key) : undefined);
    const actions = (i18n.getResource(i18n.language, 'console', 'audit.actions') ?? {}) as Readonly<Record<string, string>>;
    return {
      action: (code) => copy(`audit.actions.${copyKey(code)}`) ?? humaniseAction(code),
      targetType: (type) => copy(`audit.targetType.${type}`) ?? humanise(type),
      actionsByLabel: new Map(Object.entries(actions).map(([key, label]) => [label.toLowerCase(), key.replaceAll('__', '.')])),
    };
  }, [t, i18n]);
}

export interface VenueRef {
  readonly id: string;
  readonly name: string;
  readonly timezone: string | undefined;
}

export interface Directory {
  venue(id: string): string | undefined;
  /** The venue's time zone, for the quick date ranges; `undefined` when the venue is not known. */
  timezone(id: string): string | undefined;
  /** Every venue named, for the id field's suggestions and the venue chooser. */
  readonly venues: readonly VenueRef[];
}

/** The organisation's venues, as this member knows them — the names and zones this screen may show. */
export function useDirectory(): Directory {
  const scope = useScopeIds();
  const venues = useMyVenues();
  return useMemo(() => {
    const own: VenueRef[] = (venues.data ?? [])
      .filter((venue) => venue.organizationId === scope.organizationId)
      .map((venue) => ({ id: venue.id, name: venue.name, timezone: venue.timezone }));
    const byId = new Map(own.map((venue) => [venue.id, venue]));
    return {
      venue: (id) => byId.get(id)?.name,
      timezone: (id) => byId.get(id)?.timezone,
      venues: own,
    };
  }, [venues.data, scope.organizationId]);
}
